import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Refs to prevent redundant state updates and API calls
  const currentTokenRef = useRef<string | null>(null);
  const adminCheckedForRef = useRef<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const updateAuth = (newSession: Session | null) => {
      if (!isMounted) return;

      const newToken = newSession?.access_token ?? null;

      // Skip if the session hasn't actually changed
      if (newToken === currentTokenRef.current) return;
      currentTokenRef.current = newToken;

      setSession(newSession);
      setUser(newSession?.user ?? null);

      if (newSession?.user) {
        const userId = newSession.user.id;
        // Only check role once per user
        if (adminCheckedForRef.current !== userId) {
          adminCheckedForRef.current = userId;
          Promise.resolve(
            supabase
              .from("user_roles")
              .select("role")
              .eq("user_id", userId)
              .eq("role", "admin")
              .maybeSingle()
          )
            .then(({ data }) => {
              if (isMounted) setIsAdmin(!!data);
            })
            .catch(() => {
              if (isMounted) setIsAdmin(false);
            });
        }
      } else {
        setIsAdmin(false);
        adminCheckedForRef.current = null;
      }
    };

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        updateAuth(newSession);
      }
    );

    // Initial load
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      updateAuth(initialSession);
      if (isMounted) setLoading(false);
    }).catch(() => {
      if (isMounted) setLoading(false);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  }, []);

  const signOut = useCallback(async () => {
    currentTokenRef.current = null;
    adminCheckedForRef.current = null;
    await supabase.auth.signOut();
    setIsAdmin(false);
  }, []);

  return (
    <AuthContext.Provider value={{ session, user, isAdmin, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
